import {t} from '@lingui/macro';
import {ReactNode} from 'react';

import {
  emojiToDefinition,
  isValidEmoji,
  unifiedToShortcode,
} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {SearchHintModel, SearchHintTypesEnum, SearchHintValue} from '../../../../models/api/searchHintModel';
import {InboxesSearchLocationsEnum} from '../../../../models/url/inboxesUrlModel';
import {baseEmojiValidationMap} from '../../../../services/emojis/emojis';
import {InboxesSearchParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  SearchLocalFilterIcon,
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';

export interface VisualSearchHintValue<T extends string = string> extends SearchHintValue {
  value: T;
  icon?: SearchLocalFilterIconType;
  tooltip?: ReactNode;
  isDefaultValue?: boolean;
}

export interface VisualSearchHint<T extends string = string> extends SearchHintModel {
  values: ReadonlyArray<VisualSearchHintValue<T>>;
  onClick?: (hint: VisualSearchHint<T>, selectedIndex: number) => Partial<InboxesSearchParameters>;
  icon: SearchLocalFilterIconType;
  selectedValueIndex: number;
}

export const serializeToVisualHint = (
  hint: SearchHintModel,
  searchQuery: string,
  localize: Localizer,
): VisualSearchHint => {
  const {title, type, values} = hint;

  const selectedValueIndexWithModifier = values.findIndex(
    ({modifier, value}) => modifier && searchQuery.includes(`${modifier}:${value}`),
  );

  switch (type) {
    case SearchHintTypesEnum.ATTACHMENT:
      return {
        ...hint,
        title: localize(t('search-hint-has-attachment')`Has attachment`),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'attachmentSmall',
          selectedIconName: 'attachmentSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
      };

    case SearchHintTypesEnum.RECIPIENT:
      return {
        ...hint,
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'participantSmall',
          selectedIconName: 'participantFilledSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
      };

    case SearchHintTypesEnum.SENT_BY_ME:
      return {
        ...hint,
        title: localize(t('search-hint-sent-by-me')`Sent by me`),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'sendSmall',
          selectedIconName: 'sendFilledSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
      };

    case SearchHintTypesEnum.TAG:
      return {
        ...hint,
        title: localize(t('search-hint-tag')`Has tag`),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'tagSmall',
          selectedIconName: 'tagFilledSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
        values: hint.values.map((val) => ({
          ...val,
          icon: computeHintTagValueIcon(val),
        })),
      };

    case SearchHintTypesEnum.UNREAD:
      return {
        ...hint,
        title: localize(t('search-hint-unread')`Is Unread`),
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'eyeCrossedSmall',
          selectedIconName: 'eyeCrossedFilledSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
      };

    // This may happen if there are new hint types on the BE that we did not implement on the FE yet.
    default:
      return {
        ...hint,
        title: title ?? values.length === 1 ? `${values[0].modifier}:${values[0].value}` : 'Dropdown hint',
        icon: {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'helpFilledSmall',
        },
        selectedValueIndex: selectedValueIndexWithModifier,
      };
  }
};

export const makeSearchFiltersVisualHint = (
  selectedSearchFilter: InboxesSearchLocationsEnum,
  localSearchFilterName: ReactNode,
  localSearchFilterIcon: SearchLocalFilterIconType,
  localize: Localizer,
): VisualSearchHint => {
  const values: ReadonlyArray<VisualSearchHintValue<InboxesSearchLocationsEnum>> = [
    {
      value: InboxesSearchLocationsEnum.LOCAL,
      // @TODO: Remove cast.
      title: localSearchFilterName as string,
      icon: localSearchFilterIcon,
    },
    {
      value: InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE,
      title: localize(t('search-hint-filter-sidebar')`My sidebar`),
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'workspaceSmall',
        selectedIconName: 'workspaceSmall',
      },
      tooltip: localize(
        t(
          'search-hint-filter-sidebar-tooltip',
        )`Show me results from inboxes, tags, views, and teammates that I've added to my sidebar.`,
      ),
    },
    {
      value: InboxesSearchLocationsEnum.GLOBAL,
      title: localize(t('search-hint-filter-all-inboxes')`All inboxes`),
      isDefaultValue: true,
      icon: {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'inboxSmall',
        selectedIconName: 'inboxFilledSmall',
      },
      tooltip: localize(
        t(
          'search-hint-filter-all-inboxes-tooltip',
        )`Show me search results from all inboxes, tags, views, and teammates that I have access to.`,
      ),
    },
  ];

  return {
    type: SearchHintTypesEnum.SEARCH_FILTERS,
    values,
    priority: 1,
    icon: {
      type: SearchLocalFilterTypesEnum.ICON,
      iconName: 'workspaceSmall',
    },
    onClick: (hint, selectedIndex) => ({location: values[selectedIndex].value}),
    selectedValueIndex: values.findIndex(({value}) => selectedSearchFilter === value),
  };
};

export function maybeReplaceSearchLocalFilterIcon(icon: SearchLocalFilterIcon, isSelected: boolean) {
  const {iconName, selectedIconName} = icon;

  if (isSelected) {
    return selectedIconName || 'checkmarkSelectedSmall';
  }

  return iconName;
}

function computeHintTagValueIcon<T extends string = string>(
  hintValue: VisualSearchHintValue<T>,
): SearchLocalFilterIconType {
  if (hintValue.emoji && isValidEmoji(hintValue.emoji, baseEmojiValidationMap)) {
    const emojiDef = emojiToDefinition(unifiedToShortcode(hintValue.emoji), baseEmojiValidationMap);

    if (emojiDef) {
      return {
        type: SearchLocalFilterTypesEnum.EMOJI,
        emoji: emojiDef,
      };
    }
  }

  return {
    type: SearchLocalFilterTypesEnum.ICON,
    iconName: 'tagSmall',
    selectedIconName: 'tagFilledSmall',
  };
}
